import React from 'react';
import PropTypes from 'prop-types';

import Card from 'common/components/card';
import CardAttributes from 'common/components/card-attributes';
import CardContent from 'common/components/card-content';
import CardHeader from 'common/components/card-header';
import ZoneFlowColors, {LinkDefaultColor} from './colors';

import Tooltip from './tooltip';

import {cycle} from 'common/utils/array-util';
import {t} from 'common/utils/translation-util';
import {ResponsiveSankey} from '@nivo/sankey';

import {
  OrientationEnum,
  ReportObjectSizeEnum,
  ReportZoneFlowColorByEnum,
  ReportZoneFlowNodeJustificationEnum,
  ReportZoneFlowOpacityEnum,
} from 'common/proxies/enum-proxy';

import './index.scss';

const LINK_OPACITY_OPTIONS = Object.values(ReportZoneFlowOpacityEnum).map(
  ({id}) => id,
);
const ORIENTATION_OPTIONS = Object.values(OrientationEnum).map(({id}) => id);
const NODE_JUSTIFICATION_OPTIONS = Object.values(
  ReportZoneFlowNodeJustificationEnum,
).map(({id}) => id);

export const parseZoneFlowLinks = (data) => {
  return data.map(([source, target, value, percent]) => ({
    source,
    target,
    value,
    percent,
    color: LinkDefaultColor,
  }));
};

export const DELIMETER = '_*_';
const _getZoneIdByNodeId = (nodeId) => nodeId.split(DELIMETER)[0];
export const parseZoneFlowNodes = (data) => {
  const uniqueNodeIds = new Set();
  for (const link of data) {
    const [source, target] = link;
    uniqueNodeIds.add(source);
    uniqueNodeIds.add(target);
  }
  return Array.from(uniqueNodeIds, (nodeId) => ({
    id: nodeId,
    label: _getZoneIdByNodeId(nodeId),
  }));
};

export const addColor = (nodes, links, colorBy) => {
  const zones = {};
  const colors = cycle(ZoneFlowColors);
  nodes.forEach((node) => {
    if (!zones[node.label]) {
      zones[node.label] = colors.next().value;
    }
    node.color = zones[node.label].node;
  });
  links.forEach((link) => {
    const {source, target} = link;
    let {color} = link;
    if (colorBy === ReportZoneFlowColorByEnum.INPUT_NODE.id) {
      color = zones[_getZoneIdByNodeId(source)].link;
    } else if (colorBy === ReportZoneFlowColorByEnum.OUTPUT_NODE.id) {
      color = zones[_getZoneIdByNodeId(target)].link;
    }
    link.color = color;
  });
};

const NODE_SPACING_VALUES = {
  [ReportObjectSizeEnum.SMALL.id]: 6,
  [ReportObjectSizeEnum.LARGE.id]: 12,
};

const NODE_WIDTH_VALUES = {
  [ReportObjectSizeEnum.SMALL.id]: 12,
  [ReportObjectSizeEnum.LARGE.id]: 24,
};

const BuildingAnalyticsZoneFlowCard = ({
  attributes,
  connectDragSource,
  disableConfig,
  height,
  labelOrientation,
  linkLabels,
  linkOpacity,
  links,
  nodeSpacing,
  nodeWidth,
  nodes,
  orientation,
  report,
}) => {
  const nodeWidthValue = NODE_WIDTH_VALUES[nodeWidth] || 1;
  const nodeSpacingValue = NODE_SPACING_VALUES[nodeSpacing] || 0;

  const subtitle = report.meta.period ? report.meta.period.displayName : '';
  const chartMargin = nodeSpacingValue;
  return (
    <Card className={'BuildingAnalyticsZoneFlowCard'}>
      {connectDragSource(
        <div>
          <CardHeader
            disableConfig={disableConfig}
            disableDownloadCsv={true}
            disableDownloadPointsCsv={true}
            priorityWarnings={report.priorityWarnings}
            reportId={report.id}
            subtitle={subtitle}
            title={report.name}
            warnings={report.warnings}
          />
        </div>,
      )}
      <CardContent>
        <div
          className={'ZoneFlowCard'}
          style={{
            height,
            position: 'relative',
          }}>
          {nodes.length && links.length ? (
            <ResponsiveSankey
              align={'justify'}
              animate={false}
              colors={(node) => node.color}
              data={{nodes, links}}
              enableLinkGradient={false}
              height={height}
              label={(node) => node.label || node.id}
              labelOrientation={labelOrientation}
              labelPosition={'inside'}
              labelTextColor={{from: 'color', modifiers: [['darker', '1.1']]}}
              layout={orientation}
              linkHoverOthersOpacity={0.1}
              linkOpacity={
                linkOpacity === ReportZoneFlowOpacityEnum.TRANSPARENT.id
                  ? 0
                  : 0.5
              }
              linkTooltip={({link}) => (
                <Tooltip
                  count={
                    linkLabels.indexOf('count') > -1 ? link.value : undefined
                  }
                  percent={
                    linkLabels.indexOf('percent') > -1
                      ? link.percent
                      : undefined
                  }
                  source={link.source}
                  target={link.target}
                />
              )}
              margin={{top: 0, left: 0, bottom: chartMargin, right: 0}}
              nodeBorderColor={{from: 'color', modifiers: [['darker', 0.8]]}}
              nodeBorderWidth={0}
              nodeOpacity={1}
              nodeSpacing={nodeSpacingValue}
              nodeThickness={nodeWidthValue}
            />
          ) : (
            t('No Data')
          )}
        </div>
        {attributes.length ? <CardAttributes attributes={attributes} /> : null}
      </CardContent>
    </Card>
  );
};

// Note: "orientation" and "labelOrientation" are not
// user configurable, so they are not passed from report
BuildingAnalyticsZoneFlowCard.propTypes = {
  attributes: PropTypes.array,
  connectDragSource: PropTypes.func,
  disableConfig: PropTypes.bool,
  height: PropTypes.number,
  labelOrientation: PropTypes.oneOf(ORIENTATION_OPTIONS),
  linkLabels: PropTypes.array,
  linkOpacity: PropTypes.oneOf(LINK_OPACITY_OPTIONS),
  links: PropTypes.array,
  nodeJustification: PropTypes.oneOf(NODE_JUSTIFICATION_OPTIONS),
  nodeSpacing: PropTypes.oneOf(Object.keys(NODE_SPACING_VALUES)),
  nodeWidth: PropTypes.oneOf(Object.keys(NODE_WIDTH_VALUES)),
  nodes: PropTypes.array,
  orientation: PropTypes.oneOf(ORIENTATION_OPTIONS),
  report: PropTypes.object.isRequired,
};

BuildingAnalyticsZoneFlowCard.defaultProps = {
  attributes: [],
  connectDragSource: (x) => x,
  disableConfig: false,
  height: 500,
  labelOrientation: OrientationEnum.HORIZONTAL.id,
  linkLabels: [],
  linkOpacity: ReportZoneFlowOpacityEnum.SOLID.id,
  links: [],
  nodeJustification: ReportZoneFlowNodeJustificationEnum.JUSTIFIED.id,
  nodeWidth: ReportObjectSizeEnum.SMALL.id,
  nodes: [],
  orientation: OrientationEnum.HORIZONTAL.id,
};

export default BuildingAnalyticsZoneFlowCard;
